import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, MessageCircle } from "lucide-react";
import BranchPickerModal from "@/components/site/BranchPickerModal";
import { api } from "@/lib/api";
import { scrollToSection } from "@/lib/scroll";

const DEFAULT_HERO = {
  image: "",
  overline: "Academia de Belleza · Puebla",
  title_line1: "Conviértete en un",
  title_emphasis: "profesional",
  title_line2: "de la",
  title_accent: "belleza",
  title_suffix: ".",
  subtitle:
    "Formación premium {bold}: estilismo, barbería, uñas acrílicas y extensión de pestañas. Profesores expertos, prácticas reales y la certificación que el mercado exige, con planes semanales accesibles.",
  subtitle_bold: "a tu alcance",
  image_caption_left: "Edición 2026",
  image_caption_right: "Inscripciones abiertas",
  stats: [
    { value: "+12k", label: "Egresados" },
    { value: "6", label: "Sucursales" },
    { value: "18+", label: "Años de trayectoria" },
  ],
};

// Render "+12k" splitting any trailing non-digit suffix in red
function renderStatValue(value) {
  if (!value) return null;
  const m = value.match(/^([+]?\d[\d,\.]*)(.*)$/);
  if (!m) return value;
  const [, num, suffix] = m;
  return (
    <>
      {num}
      {suffix && <span className="text-[#c40000]">{suffix}</span>}
    </>
  );
}

// Render subtitle replacing {bold} with a strong span
function renderSubtitle(text, bold) {
  if (!text) return null;
  const parts = text.split("{bold}");
  return parts.map((p, i) => (
    <React.Fragment key={i}>
      {p}
      {i < parts.length - 1 && bold && (
        <span className="font-semibold text-[#0a0a0a]">{bold}</span>
      )}
    </React.Fragment>
  ));
}

const go = (id) => scrollToSection(id);

const fadeUp = {
  initial: { opacity: 0, y: 24, filter: "blur(8px)" },
  animate: { opacity: 1, y: 0, filter: "blur(0px)" },
};

export default function Hero() {
  const [waPickerOpen, setWaPickerOpen] = useState(false);
  const [hero, setHero] = useState(DEFAULT_HERO);

  useEffect(() => {
    api
      .get("/site/hero")
      .then((r) => setHero({ ...DEFAULT_HERO, ...r.data }))
      .catch(() => {});
  }, []);

  const handleBranchWA = (branch) => {
    const msg = encodeURIComponent(
      `Hola ${branch.name}, quiero información sobre los cursos del Instituto Europa.`
    );
    window.open(`https://wa.me/${branch.whatsapp}?text=${msg}`, "_blank", "noopener");
    setWaPickerOpen(false);
  };

  return (
    <section
      data-testid="hero-section"
      className="relative pt-24 sm:pt-28 lg:pt-28 pb-2 md:pb-4 lg:pb-4 overflow-hidden"
    >
      <div className="container-ie">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          {/* Left: text */}
          <div className="lg:col-span-7 order-2 lg:order-1">
            <motion.div
              {...fadeUp}
              transition={{ duration: 0.7, delay: 0.05 }}
              className="flex items-center gap-3 mb-6"
            >
              <span className="dot-red" />
              <span className="overline">{hero.overline}</span>
            </motion.div>

            <motion.h1
              {...fadeUp}
              transition={{ duration: 0.85, delay: 0.12 }}
              className="font-display font-bold tracking-tighter text-[2.6rem] sm:text-5xl md:text-6xl lg:text-7xl leading-[1.02] text-[#0a0a0a]"
              data-testid="hero-title"
            >
              {hero.title_line1}{" "}
              <span className="italic font-light">{hero.title_emphasis}</span>
              <br />
              {hero.title_line2}{" "}
              <span className="text-[#c40000]">{hero.title_accent}</span>
              {hero.title_suffix}
            </motion.h1>

            <motion.p
              {...fadeUp}
              transition={{ duration: 0.7, delay: 0.25 }}
              className="mt-8 max-w-xl text-base md:text-lg font-light leading-relaxed text-[#525252]"
              data-testid="hero-subtitle"
            >
              {renderSubtitle(hero.subtitle, hero.subtitle_bold)}
            </motion.p>

            <motion.div
              {...fadeUp}
              transition={{ duration: 0.7, delay: 0.38 }}
              className="mt-10 flex flex-wrap items-center gap-3"
            >
              <button
                data-testid="hero-cta-courses"
                onClick={() => go("cursos")}
                className="btn-primary"
              >
                Ver Cursos <ArrowRight size={16} strokeWidth={1.8} />
              </button>
              <button
                data-testid="hero-cta-enroll"
                onClick={() => go("contacto")}
                className="btn-secondary"
              >
                Programar visita
              </button>
              <button
                type="button"
                data-testid="hero-cta-whatsapp"
                onClick={() => setWaPickerOpen(true)}
                className="btn-whatsapp"
              >
                <MessageCircle size={16} strokeWidth={1.8} /> WhatsApp
              </button>
            </motion.div>

            {/* Stats strip */}
            <motion.div
              {...fadeUp}
              transition={{ duration: 0.7, delay: 0.55 }}
              className="mt-10 md:mt-14 grid grid-cols-3 gap-6 max-w-xl border-t border-black/10 pt-6 md:pt-8"
              data-testid="hero-stats"
            >
              {(hero.stats || []).slice(0, 3).map((s, i) => (
                <div key={i}>
                  <div className="font-display font-bold text-3xl md:text-4xl text-[#0a0a0a]">
                    {renderStatValue(s.value)}
                  </div>
                  <div className="mt-1 text-xs uppercase tracking-[0.18em] text-[#525252]">
                    {s.label}
                  </div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right: image (hidden on mobile) */}
          <motion.div
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="hidden lg:block lg:col-span-5 order-1 lg:order-2 relative"
          >
            <div className="relative w-full overflow-hidden bg-[#fafafa] lg:max-h-[520px] xl:max-h-[560px]">
              {hero.image && (
                <img
                  src={hero.image}
                  alt="Profesional de la belleza"
                  className="w-full h-auto block lg:h-[520px] xl:h-[560px] lg:object-cover lg:object-top"
                  data-testid="hero-image"
                />
              )}
              {hero.image && (
                <div className="absolute left-4 right-4 bottom-4 flex items-center justify-between bg-white/85 backdrop-blur-md px-4 py-2 text-[#0a0a0a] text-[0.7rem] uppercase tracking-[0.2em]">
                  <span>{hero.image_caption_left}</span>
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-[#c40000] rounded-full" /> {hero.image_caption_right}
                  </span>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </div>

      <BranchPickerModal
        open={waPickerOpen}
        mode="whatsapp"
        course={null}
        onClose={() => setWaPickerOpen(false)}
        onSelect={handleBranchWA}
      />
    </section>
  );
}
